import axios from 'axios';
import { apiUrl } from '@/api/config';
import { getAuthToken, getUserData } from '@/utils/auth';
import { toast } from 'sonner';
import { trackEvent, attachUserProps } from '@/analytics/analytics';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';

export const openStripeCheckout = async (checkoutUrl, onFinished) => {
  if (!checkoutUrl) return;

  if (Capacitor.isNativePlatform()) {
    let listener = null;
    try {
      if (typeof onFinished === 'function') {
        listener = await Browser.addListener('browserFinished', () => {
          if (listener) {
            listener.remove();
            listener = null;
          }
          onFinished();
        });
      }

      await Browser.open({
        url: checkoutUrl,
        presentationStyle: 'fullscreen',
        toolbarColor: '#0d1f1a',
      });
      return;
    } catch (err) {
      console.error('Failed to open Stripe checkout with Browser plugin:', err);
      if (listener) {
        listener.remove();
      }
      window.location.href = checkoutUrl;
      return;
    }
  }

  window.location.href = checkoutUrl;
};

export const getSafeAreaInsets = () => {
  let top = 0;
  let bottom = 0;
  let left = 0;
  let right = 0;

  if (typeof window !== 'undefined' && window.AndroidSafeAreaBridge) {
    try {
      top = window.AndroidSafeAreaBridge.getTopInset?.() || 0;
      bottom = window.AndroidSafeAreaBridge.getBottomInset?.() || 0;
      left = window.AndroidSafeAreaBridge.getLeftInset?.() || 0;
      right = window.AndroidSafeAreaBridge.getRightInset?.() || 0;
    } catch (e) {}
  }

  // Probe CSS env() values using padding to avoid layout-derived bottom inflation
  if (typeof document !== 'undefined') {
    try {
      const probe = document.createElement('div');
      probe.style.cssText = 'position:fixed;top:0;left:0;padding-top:env(safe-area-inset-top,0px);padding-bottom:env(safe-area-inset-bottom,0px);visibility:hidden;pointer-events:none;z-index:-1;';
      document.documentElement.appendChild(probe);
      const computed = window.getComputedStyle(probe);
      const envTop = parseFloat(computed.paddingTop) || 0;
      const envBottom = parseFloat(computed.paddingBottom) || 0;
      document.documentElement.removeChild(probe);
      if (envTop > top) top = envTop;
      if (envBottom > bottom) bottom = envBottom;
    } catch (e) {}
  }

  // iOS detection (WKWebView / Capacitor / Safari)
  const isIOS = (typeof navigator !== 'undefined' && (
    /iPad|iPhone|iPod/.test(navigator.userAgent || '') ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  )) || (typeof window !== 'undefined' && window.Capacitor?.isNativePlatform?.() && window.Capacitor?.getPlatform?.() === 'ios');

  if (isIOS) {
    if (!top || top < 20) {
      const screenH = typeof window !== 'undefined' ? Math.max(window.screen.height, window.screen.width) : 0;
      if (screenH >= 852) {
        top = 54; // Dynamic Island (iPhone 14 Pro, 15, 16)
      } else if (screenH >= 812) {
        top = 47; // Notch devices (iPhone X, 11, 12, 13, 14)
      } else {
        top = 24; // Legacy iPhones / iPads
      }
    }
    if (!bottom || bottom < 34) {
      const screenH = typeof window !== 'undefined' ? Math.max(window.screen.height, window.screen.width) : 0;
      if (screenH >= 812) {
        bottom = 34; // Home indicator
      }
    }
  }

  // Android fallback
  const isAndroid = (typeof navigator !== 'undefined' && /android/i.test(navigator.userAgent || '')) ||
    (typeof window !== 'undefined' && window.Capacitor?.isNativePlatform?.() && window.Capacitor?.getPlatform?.() === 'android');

  if (isAndroid) {
    if (!top || top <= 0) top = 36;
    if (!bottom || bottom <= 0) bottom = 48;
  }

  return { top, bottom, left, right };
};

export const applyRazorpaySafeArea = () => {
  const enforceStyles = () => {
    const { top, bottom } = getSafeAreaInsets();
    
    // Sync CSS variables to root document
    if (typeof document !== 'undefined' && document.documentElement) {
      if (top > 0) document.documentElement.style.setProperty('--safe-area-inset-top', `${top}px`);
      if (bottom > 0) document.documentElement.style.setProperty('--safe-area-inset-bottom', `${bottom}px`);
    }

    const containers = document.querySelectorAll('.razorpay-container');
    containers.forEach((container) => {
      container.style.setProperty('position', 'fixed', 'important');
      
      const topVal = top > 0 
        ? `${top}px` 
        : 'max(env(safe-area-inset-top, 47px), var(--safe-area-inset-top, 47px))';
      const bottomVal = bottom > 0 
        ? `${bottom}px` 
        : 'max(env(safe-area-inset-bottom, 34px), var(--safe-area-inset-bottom, 34px), 34px)';

      container.style.setProperty('top', topVal, 'important');
      container.style.setProperty('bottom', bottomVal, 'important');
      container.style.setProperty('left', '0', 'important');
      container.style.setProperty('right', '0', 'important');
      container.style.setProperty('width', '100%', 'important');
      container.style.setProperty('height', `calc(100% - ${topVal} - ${bottomVal})`, 'important');
      container.style.setProperty('max-height', `calc(100% - ${topVal} - ${bottomVal})`, 'important');
      container.style.setProperty('box-sizing', 'border-box', 'important');
      container.style.setProperty('z-index', '2147483647', 'important');
      container.style.setProperty('overflow', 'hidden', 'important');

      // Crucial: Constrain iframe so Razorpay's internal resize handler cannot stretch it past the container
      const iframes = container.querySelectorAll('iframe');
      iframes.forEach((iframe) => {
        iframe.style.setProperty('position', 'absolute', 'important');
        iframe.style.setProperty('top', '0', 'important');
        iframe.style.setProperty('left', '0', 'important');
        iframe.style.setProperty('bottom', '0', 'important');
        iframe.style.setProperty('right', '0', 'important');
        iframe.style.setProperty('width', '100%', 'important');
        iframe.style.setProperty('height', '100%', 'important');
        iframe.style.setProperty('max-height', '100%', 'important');
        iframe.style.setProperty('min-height', '0', 'important');
        iframe.style.setProperty('box-sizing', 'border-box', 'important');
      });
    });

    const standaloneIframes = document.querySelectorAll('iframe.razorpay-checkout-frame');
    standaloneIframes.forEach((iframe) => {
      if (iframe.closest('.razorpay-container')) {
        iframe.style.setProperty('position', 'absolute', 'important');
        iframe.style.setProperty('top', '0', 'important');
        iframe.style.setProperty('left', '0', 'important');
        iframe.style.setProperty('bottom', '0', 'important');
        iframe.style.setProperty('right', '0', 'important');
        iframe.style.setProperty('width', '100%', 'important');
        iframe.style.setProperty('height', '100%', 'important');
        iframe.style.setProperty('max-height', '100%', 'important');
        iframe.style.setProperty('min-height', '0', 'important');
        iframe.style.setProperty('box-sizing', 'border-box', 'important');
      }
    });
  };

  enforceStyles();
  const observer = new MutationObserver(() => {
    enforceStyles();
  });

  observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['style', 'class'] });

  // Frequent polling during initial 5s while Razorpay iframe injects and animates in
  const intervalId = setInterval(enforceStyles, 250);
  setTimeout(() => {
    clearInterval(intervalId);
  }, 5000);

  setTimeout(() => {
    observer.disconnect();
  }, 90000);

  return observer;
};


export const loadRazorpay = () => {
  return new Promise((resolve) => {
    // Check if already loaded
    if (window.Razorpay) {
      resolve(true);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

export const initiateCheckout = async (planId, billingCycle, portal, setLoader, couponCode = null, navigate = null) => {
  if (setLoader) setLoader(true);
  try {
    const token = getAuthToken(portal);
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const cycleUpper = (billingCycle || 'MONTHLY').toUpperCase();
    const normalizedCycle = (cycleUpper === 'QUARTERLY' || cycleUpper === 'THREE_MONTHS' || cycleUpper === '3M')
      ? 'THREE_MONTHS'
      : (cycleUpper === 'HALF_YEARLY' || cycleUpper === 'SIX_MONTHS' || cycleUpper === '6M')
      ? 'SIX_MONTHS'
      : cycleUpper;

    try {
      trackEvent('checkout_started', attachUserProps({
        page: 'checkout',
        plan_id: planId,
        billing_cycle: normalizedCycle,
        coupon_code: couponCode || null,
      }));
    } catch {}

    const response = await axios.post(apiUrl('superadmin/stripe/checkout/'), {
      plan_id: planId,
      billing_cycle: normalizedCycle,
      coupon_code: couponCode
    }, { headers });

    const data = response.data;

    if (data.gateway === 'STRIPE' && data.checkout_url) {
      await openStripeCheckout(data.checkout_url, () => {
        if (setLoader) setLoader(false);
      });
      if (setLoader) setLoader(false);
    } else if (data.gateway === 'RAZORPAY' && data.subscription_id) {
      const res = await loadRazorpay();
      if (!res) {
        toast.error('Razorpay SDK failed to load. Are you online?');
        if (setLoader) setLoader(false);
        return;
      }

      // Build a dynamic description showing mandate details for discounted subscriptions
      const currencySymbols = {
        'INR': '₹',
        'SAR': 'SR',
        'QAR': 'QR',
        'AED': 'AED',
        'KWD': 'KD',
        'BHD': 'BD',
        'OMR': 'RO',
        'GBP': '£',
        'USD': '$'
      };
      const symbol = currencySymbols[data.currency] || data.currency || '₹';

      let rzp_description = `Subscription: ${data.plan_name}`;
      if (data.start_at_display) {
        rzp_description = `Pay ${symbol}${data.discounted_price} today. ${symbol}${data.recurring_amount} ${data.billing_cycle.toLowerCase()} starts ${data.start_at_display}.`;
      }

      // Fix: Add a heartbeat monitor to auto-redirect if the popup gets stuck
      // This rescues the user if Razorpay's internal callback page crashes (SyntaxError 't')
      let monitorInterval = null;
      let monitorTimeout = null;

      const startStatusMonitor = () => {
        if (monitorInterval) return;
        monitorInterval = setInterval(async () => {
          try {
            const checkRes = await axios.get(apiUrl('users/subscription/'), { headers });
            const currentPlan = checkRes.data?.plan_name || 'Free';
            const isActivated = (currentPlan.trim().toLowerCase() === data.plan_name.trim().toLowerCase());

            if (isActivated) {
              console.log("Stuck popup rescue: Subscription detected as active! Redirecting...");
              if (monitorInterval) clearInterval(monitorInterval);
              clearTimeout(monitorTimeout);
              const successUrl = `/subscription/success?razorpay_payment_id=${data.subscription_id}&target_plan=${encodeURIComponent(data.plan_name)}`;
              if (navigate) navigate(successUrl);
              else window.location.href = `/#${successUrl}`;
            }
          } catch (e) {
            console.warn("Status monitor check failed", e);
          }
        }, 3000); // Check every 3 seconds
      };

      const options = {
        key: data.publishable_key || data.key_id,
        subscription_id: data.subscription_id,
        name: 'Budlee AI',
        description: rzp_description,
        handler: function (response) {
          if (monitorInterval) clearInterval(monitorInterval);
          clearTimeout(monitorTimeout);
          try {
            trackEvent('subscription_activated', attachUserProps({
              page: 'checkout',
              gateway: 'RAZORPAY',
              plan_name: data.plan_name,
              billing_cycle: data.billing_cycle,
            }));
          } catch {}
          // Fix: Redirect immediately to verification using navigate for instant transition
          const successUrl = `/subscription/success?razorpay_payment_id=${response.razorpay_payment_id}&target_plan=${encodeURIComponent(data.plan_name)}`;
          if (navigate) {
            navigate(successUrl);
          } else {
            window.location.href = `/#${successUrl}`;
          }
        },
        prefill: {
          name: data.customer_name?.includes('@') ? (getUserData(portal)?.full_name || data.customer_name) : data.customer_name,
          email: data.customer_email,
          contact: data.customer_contact || ''
        },
        notes: {
          plan_name: data.plan_name,
          recurring_amount: data.recurring_amount,
          billing_cycle: data.billing_cycle,
          starts_at: data.start_at_display || "immediately"
        },
        theme: {
          color: '#3B82F6',
          // Fix: Allow retry for bank-side transient failures
          retry: true
        },
        modal: {
          backdropclose: true, // Allow closing by clicking outside if stuck
          escape: true,
          handleback: false,
          // Fix: Handle manual popup closure to reset UI
          ondismiss: function() {
            if (monitorInterval) clearInterval(monitorInterval);
            clearTimeout(monitorTimeout);
            if (setLoader) setLoader(false);
            try {
              trackEvent('checkout_abandoned', attachUserProps({
                page: 'checkout',
                gateway: 'RAZORPAY',
                plan_name: data.plan_name,
                billing_cycle: data.billing_cycle,
                reason: 'modal_dismissed',
              }));
            } catch {}
            console.log("Razorpay checkout was closed by user");
          }
        }
      };

      const rzp = new window.Razorpay(options);

      // Start the monitor if the popup hasn't closed after 15 seconds (likely stuck)
      monitorTimeout = setTimeout(startStatusMonitor, 15000);

      rzp.on('payment.failed', function(response) {
          console.log("Payment specifically failed:", response.error);
          if (monitorInterval) clearInterval(monitorInterval);
          clearTimeout(monitorTimeout);
          if (setLoader) setLoader(false);
          try {
            trackEvent('payment_failed', attachUserProps({
              page: 'checkout',
              gateway: 'RAZORPAY',
              plan_name: data.plan_name,
              billing_cycle: data.billing_cycle,
              error: response?.error?.description || 'Payment failed',
            }));
          } catch {}
      });

      applyRazorpaySafeArea();
      rzp.open();
      if (setLoader) setLoader(false);
    } else if (data.gateway === 'MOCK') {
      toast.info('Mock gateway selected. Activating...');
      try {
        await axios.post(apiUrl('superadmin/stripe/mock-subscribe/'), {
          plan_id: planId,
          billing_cycle: billingCycle?.toUpperCase() || 'MONTHLY',
          coupon_code: couponCode
        }, { headers });
        try {
          trackEvent('subscription_activated', attachUserProps({
            page: 'checkout',
            gateway: 'MOCK',
            plan_id: planId,
            billing_cycle: billingCycle?.toUpperCase() || 'MONTHLY',
          }));
        } catch {}
        
        const successUrl = `/subscription/success?target_plan=${encodeURIComponent(data.plan_name || 'Premium')}`;
        if (navigate) {
            navigate(successUrl);
        } else {
            window.location.href = `/#${successUrl}`;
        }
      } catch (mockError) {
        console.error('Mock activation error:', mockError);
        toast.error('Failed to activate mock subscription');
        if (setLoader) setLoader(false);
      }
    }
  } catch (error) {
    console.error('Checkout error:', error);
    try {
      trackEvent('payment_failed', attachUserProps({
        page: 'checkout',
        plan_id: planId,
        billing_cycle: billingCycle?.toUpperCase() || 'MONTHLY',
        error: error.response?.data?.error || 'Failed to start checkout',
      }));
    } catch {}
    toast.error(error.response?.data?.error || 'Failed to start checkout');
    if (setLoader) setLoader(false);
  }
};
